/**
 * candidatePropertyJoin — PURE candidate↔registry-property join (AD-013 / L1A).
 *
 * The functional gap (L1A clean-flow spec §3): the candidate a staff member
 * grades lives in `intake/{tier}_R1.answers.properties[]` (a `PropertyInput`
 * with an optional `propertyId` FK), while the linked, gridded registry
 * property lives in `projects/{pid}.propertyRefs[]`. Linking a property does
 * NOT stamp `answers.properties[i].propertyId` — it is only set by a prior
 * "Push to registry". So the candidate the staff marks is divorced from the
 * `P#####` the owner gridded, and the marking surface / report can never
 * resolve the grid.
 *
 * This helper stamps each UNJOINED candidate with a linked registry property —
 * but ONLY when the mapping is unambiguous. It NEVER silently mis-joins: any
 * residual ambiguity is returned in `ambiguous[]` for a staff "link candidate →
 * P#####" picker (the locked `feedback_contact_link_manual_search` rule, applied
 * to properties: auto-match is insufficient, always give staff a manual link).
 *
 * Pure — no Firestore, no React. Fully unit-tested. The two consumers
 * (StaffTechnicalPanel load/link effect + PropertyLinkingPanel write-through)
 * call this, then persist the `joined` array via `saveProperties` when `changed`.
 */
import type { PropertyInput } from './staffIntake';

/** Minimal shape of a `projects/{pid}.propertyRefs[]` entry. */
export interface PropertyRefLike {
  propertyId: string;
}

export interface JoinResult {
  /** Candidates with `propertyId` stamped where the join was unambiguous.
   *  Same array (by value) as the input when nothing changed. */
  joined: PropertyInput[];
  /** True when at least one candidate gained a `propertyId`. */
  changed: boolean;
  /** Linked registry propertyIds that could NOT be auto-assigned without guessing
   *  — surfaced to staff for a manual candidate→P##### link. */
  ambiguous: string[];
}

/**
 * Stamp each unjoined candidate with a linked registry property, unambiguously.
 *
 * Rules:
 *  - A candidate that already carries a `propertyId` is left untouched, and that
 *    property is considered CLAIMED (never re-assigned to another candidate).
 *  - A linked property already claimed by some candidate is skipped.
 *  - SINGLE-CANDIDATE RECONCILE (the common L1A case): exactly one unjoined
 *    candidate and exactly one free linked property ⇒ stamp it.
 *  - CLEAN 1:1 BY ORDER: the count of unjoined candidates equals the count of
 *    free linked properties (and is > 1) ⇒ stamp by position. (Staff see an
 *    "auto-linked" note and can correct via the manual picker.)
 *  - Anything else (counts don't line up) ⇒ no auto-stamp; the free properties
 *    are returned in `ambiguous[]`.
 */
export function joinCandidatesToLinkedProperties(
  candidates: PropertyInput[],
  propertyRefs: PropertyRefLike[],
): JoinResult {
  const cands = Array.isArray(candidates) ? candidates : [];
  const refs = Array.isArray(propertyRefs) ? propertyRefs : [];

  // propertyIds already stamped on some candidate → claimed, never reused.
  const claimed = new Set(
    cands.map((c) => c?.propertyId).filter((id): id is string => !!id),
  );

  // free linked properties = refs not already claimed (de-duped, valid ids).
  const seen = new Set<string>();
  const free: string[] = [];
  for (const r of refs) {
    const id = r?.propertyId;
    if (!id || claimed.has(id) || seen.has(id)) continue;
    seen.add(id);
    free.push(id);
  }

  // indices of candidates with no propertyId yet.
  const unjoinedIdx: number[] = [];
  cands.forEach((c, i) => {
    if (!c?.propertyId) unjoinedIdx.push(i);
  });

  // Nothing to reconcile.
  if (free.length === 0 || unjoinedIdx.length === 0) {
    return { joined: cands, changed: false, ambiguous: [] };
  }

  // Unambiguous when the counts line up exactly (1↔1 or N↔N by order).
  if (unjoinedIdx.length === free.length) {
    const joined = cands.slice();
    unjoinedIdx.forEach((i, k) => {
      joined[i] = { ...joined[i], propertyId: free[k] };
    });
    return { joined, changed: true, ambiguous: [] };
  }

  // Counts don't line up → don't guess. Leave the free properties for the
  // manual candidate→P##### picker.
  return { joined: cands, changed: false, ambiguous: free };
}

/**
 * GATE for the L1A candidate AUTO-JOIN persist (owner bug 2026-07-05, the
 * "marks vanish on refresh" data-loss). The auto-join effect stamps an unjoined
 * candidate with its linked registry property and PERSISTS the result via
 * `saveProperties` (a wholesale `answers.properties` array-replace + the
 * `answers.floors` mirror). It MUST NOT run until the intake load has replaced
 * the panel's INITIAL `[blankProperty()]` placeholder with the real, persisted
 * candidate list.
 *
 * THE BUG IT PREVENTS: on the mount commit the panel state is still the initial
 * `props_ = [blankProperty()]` (one UNSTAMPED, `floors:[]` slot) and `loading`
 * is stale-`false` (the load effect's `setLoading(true)` has not re-rendered
 * yet — every effect in the same commit reads the pre-load render's state). The
 * auto-join therefore fired against that placeholder, `joinCandidatesToLinked­
 * Properties` stamped it with the linked `P#####`, and the effect PERSISTED a
 * BRAND-NEW blank candidate (`floors:[]`, a fresh random `newId('prop')`) —
 * clobbering the real candidate AND its saved doors/AUOs/markups on EVERY mount,
 * and churning the candidate id (`prop_4xavn5` → `prop_izpo8p` …) each time. On
 * a refresh right after a good save, this remount write is what wipes the marks.
 *
 * `loaded` (a ref the load effect flips true only after `setProps_` lands the
 * real list) closes the window: pre-load the gate is false → the effect is a
 * no-op; the effect re-runs when the load completes (its `loading`/`props_`
 * deps change) and then joins the REAL candidate. Pure — no Firestore, no React.
 */
export function autoJoinMayPersist(opts: {
  hideGridTool: boolean;
  isL1A: boolean;
  clientId: string | null;
  loading: boolean;
  removeBusy: boolean;
  loaded: boolean;
}): boolean {
  return !!(
    opts.hideGridTool &&
    opts.isL1A &&
    opts.clientId &&
    !opts.loading &&
    !opts.removeBusy &&
    opts.loaded
  );
}

/**
 * READ-SIDE visibility rule — the join's inverse (owner ruling 2026-07-03:
 * "a correction must propagate"). A candidate whose stamped `propertyId` FK
 * points at a registry property that is NO LONGER linked on the project
 * (`projects.propertyRefs[]`) was corrected away at Intake (unlinked) — every
 * link-derived read surface (the Evaluate candidate list, the grid-analysis
 * summary, the stage-state derivation) must DROP it, live. A candidate with no
 * FK yet (a raw client-submitted candidate slot) is always visible — it never
 * claimed a registry property, so no unlink can invalidate it.
 *
 * `linkedPropertyIds` shape decides the mode (server mirror: composer
 * loadInputs.js::filterUnlinkedCandidates — functions 8c779d8):
 *   · NON-EMPTY list → the project's live link list; filter by it. The only
 *     shape carrying real unlink information — staff corrected the link set
 *     and ≥1 property is still linked, so a stamped FK that fell out of the
 *     list was genuinely unlinked ⇒ drop.
 *   · EMPTY list → NO live link data ⇒ NO-OP (every candidate visible).
 *
 * ── VP678 POST-MORTEM (2026-07-03, live regression) ──────────────────────────
 * The first landing read `[]` as "staff unlinked everything ⇒ hide every FK'd
 * candidate". REAL prod data disproved that: projects/VP678 carries
 * propertyRefs: [] (field present, EMPTY) while its intake holds two
 * registry-stamped, fully evaluated candidates — the candidate-STAMP writes
 * (pickCandidateProperty / savePropertyLink) are a DIFFERENT WRITE LANE that
 * never maintains projects.propertyRefs[]. An empty refs list is therefore
 * ambiguous ("refs lane unused on this project" vs "staff unlinked the last
 * one"), and the tie goes to never-hide: no data → no-op — owner-evaluated
 * content is never suppressed on the weaker signal. Accepted residual:
 * unlinking the LAST linked property no longer hides its candidate (the
 * normal correction — unlink one of N, list stays non-empty — still drops).
 * Entries without a usable string id are ignored, so a garbage-shaped refs
 * projection also degrades to no-op, never to hide-everything.
 *
 * NON-DESTRUCTIVE by design: this filters READS only. The intake doc keeps the
 * candidate slot (with its floors / gradings), so re-linking the same property
 * restores the row exactly as it was. Pure — no Firestore, no React.
 */
export function isCandidateLinkVisible(
  candidate: { propertyId?: string } | null | undefined,
  linkedPropertyIds: readonly string[],
): boolean {
  const fk = candidate?.propertyId;
  if (!fk) return true;
  // Normalise to usable string ids; an EMPTY result = no link data = NO-OP
  // (the VP678 rule above) — never hide on the weaker signal.
  const ids = Array.isArray(linkedPropertyIds)
    ? linkedPropertyIds.filter(
        (id): id is string => typeof id === 'string' && id.length > 0,
      )
    : [];
  if (ids.length === 0) return true;
  return ids.includes(fk);
}

/**
 * WRITE-SIDE stamp clear — the join's true inverse (owner bug 2026-07-03,
 * two-lanes fix). `unlinkPropertyFromProject` removes the Lane-B registry link
 * (`projects.propertyRefs[]` + `properties.engagementRefs[]`) but the Lane-A
 * candidate STAMP (`intake/{tier}.answers.properties[i].propertyId`) survived,
 * so Intake kept showing "✓ auto-linked P#####" for a property that was
 * unlinked (or even trashed from the registry). This pure planner strips the
 * stamp from every candidate carrying that FK; the impure companion
 * (`staffIntake.clearCandidatePropertyStamp`) persists the result via the same
 * `saveProperties` array-replace every other candidate write uses.
 *
 * ALSO RESETS THE FLOORS (owner bug 2026-07-05, "the new property inherits the
 * OLD property's marks"). A candidate's `floors[]` (boundary + doors + AUOs +
 * markups) are PROPERTY-SPECIFIC — every door/AUO/markup is placed on ONE
 * registry property's grid (image-pixel space + a `gridId` FK). The moment the
 * property is unlinked the slot no longer evaluates it, so those marks are
 * orphaned. Keeping them here was the leak: the slot survives unjoined WITH its
 * old floors, and re-linking a DIFFERENT property re-stamps the SAME slot
 * (auto-join / planCandidateForLink ABSORB the unjoined candidate) — inheriting
 * the previous property's doors/AUOs/markups (VP676: `prop_izpo8p` kept P00007's
 * marks after the owner switched it to P00006). Clearing the floors here means
 * the re-link starts fresh and the boundary re-hydrates from the NEW property's
 * registry grid (the panel's seed effect). The slot identity + label + address +
 * recommendation are untouched; only the property-bound marks reset.
 *
 * Tolerates the FK being absent (`changed: false`, same array back) — the VP678
 * case where the refs lane never carried the link is a clean no-op. Pure — no
 * Firestore, no React.
 */
export function clearCandidateStamps(
  candidates: PropertyInput[],
  propertyId: string,
): { next: PropertyInput[]; changed: boolean } {
  const cands = Array.isArray(candidates) ? candidates : [];
  if (!propertyId || !cands.some((c) => c?.propertyId === propertyId)) {
    return { next: cands, changed: false };
  }
  const next = cands.map((c) => {
    if (c?.propertyId !== propertyId) return c;
    // drop the key entirely (propertyToAnswer only persists a truthy string,
    // so the stored row loses the FK on the next save) AND reset the
    // property-specific marks so a re-link cannot inherit them.
    const { propertyId: _dropped, ...rest } = c;
    return { ...rest, floors: [] } as PropertyInput;
  });
  return { next, changed: true };
}

/**
 * PURE — stamp a candidate slot with `propertyId`, RESETTING its property-
 * specific marks when the property actually CHANGES (owner bug 2026-07-05). Used
 * by the direct re-stamp path (the manual candidate→P##### picker) so switching
 * a slot from one linked property to another cannot carry the previous
 * property's floors/doors/AUOs/markups (same invariant as `clearCandidateStamps`
 * — marks are bound to ONE registry grid). Floors are cleared ONLY on a true
 * change (a previously-stamped slot moving to a DIFFERENT id); a first-time stamp
 * of an unjoined slot (no prior FK) keeps whatever it had — an unlinked slot
 * already had its floors emptied by `clearCandidateStamps`, and a genuinely fresh
 * slot carries no property-bound marks. Re-stamping the SAME id is a no-op patch.
 */
export function stampCandidateProperty(
  candidate: PropertyInput,
  propertyId: string,
): PropertyInput {
  const prev = candidate?.propertyId;
  const propertyChanged = !!prev && prev !== propertyId;
  return {
    ...candidate,
    propertyId,
    ...(propertyChanged ? { floors: [] } : {}),
  };
}
